import { Component, OnDestroy, OnInit } from '@angular/core';
import { BangumiProviderService } from '../../bangumi-provider.service';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ListEditorComponent } from '../../ui/list-editor/list-editor.component';
import { MultiSelectComponent } from '../../ui/multi-select/multi-select.component';
import { OAuth2Client } from '../../../sdk/bangumiProvider';
import { SingleSelectComponent } from '../../ui/single-select/single-select.component';
import { JsonPipe, NgClass } from '@angular/common';
import { UIToast, UIToastComponent, UIToastRef } from '@irohalab/deneb-ui';

const ALLOWED_GRANT_TYPES = ['authorization_code', 'refresh_token', 'client_credentials', 'urn:ietf:params:oauth:grant-type:device_code', 'urn:ietf:params:oauth:grant-type:jwt-bearer'];
const ALLOWED_TOKEN_ENDPOINT_AUTH_METHODS = ['client_secret_basic', 'client_secret_post', 'private_key_jwt', 'none'];

@Component({
    selector: 'app-oauth2-client-edit',
    templateUrl: './oauth2-client-edit.component.html',
    imports: [
    ReactiveFormsModule,
    ListEditorComponent,
    MultiSelectComponent,
    SingleSelectComponent,
    NgClass,
    JsonPipe,
    RouterLink
],
    styleUrl: './oauth2-client-edit.component.less'
})
export class OAuth2ClientEditComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();
  private toastRef!: UIToastRef<UIToastComponent>;

  client!: OAuth2Client;
  submitted = false;

  editMode: boolean = false;
  clientForm!: FormGroup;
  allowedGrantTypes = ALLOWED_GRANT_TYPES;
  allowedTokenEndpointAuthMethods = ALLOWED_TOKEN_ENDPOINT_AUTH_METHODS;
  tokenEndpointAuthMethod!: string;
  get tokenEndpointHintText(): string {
    switch (this.tokenEndpointAuthMethod) {
      case 'client_secret_basic':
        return 'Send client_id and client_secret as application/x-www-form-urlencoded encoded in the HTTP Authorization header.';
      case 'client_secret_post':
        return 'Send client_id and client_secret as application/x-www-form-urlencoded in the HTTP body.';
      case 'private_key_jwt':
        return 'Use JSON Web Tokens to authenticate the client.';
      case 'none':
        return 'Used for public clients (native apps, mobile apps) which can not have secrets.';
      default:
        return '';
    }
  }

  constructor(private bangumiProvider: BangumiProviderService,
              private route: ActivatedRoute,
              private router: Router,
              private formBuilder: FormBuilder,
              toast: UIToast) {
    this.toastRef = toast.makeText();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    const paramMap = this.route.snapshot.paramMap;
    const clientId = paramMap.get('id') as string;
    const editMode = this.route.snapshot.queryParamMap.get('mode');
    if (editMode) {
      this.editMode = editMode === 'edit';
    }
    if (this.editMode) {
      this.subscription.add(
        this.bangumiProvider.getMyOAuth2Client(clientId)
          .subscribe({
            next: (data) => {
              this.client = data;
              this.clientForm = this.formBuilder.group({
                allowed_cors_origins: [[...data.allowed_cors_origins?.slice() || []], Validators.required],
                audience: [data.audience?.slice(), Validators.required],
                client_name: [data.client_name?.slice(), Validators.required],
                client_secret: [data.client_secret],
                client_uri: [data.client_uri],
                contacts: [[...data.contacts?.slice() || []], Validators.required],
                grant_types: [[...data.grant_types?.slice() || []], Validators.minLength(1)],
                logo_uri: [data.logo_uri],
                policy_uri: [data.policy_uri],
                redirect_uris: [[...data.redirect_uris?.slice() || []]],
                scope: [data.scope?.split(' ') || []],
                token_endpoint_auth_method: [data.token_endpoint_auth_method, Validators.required],
                tos_uri: [data.tos_uri],
                // response_types: [data.response_types]
              });
            },
            error: (err) => {
            }
          })
      );
    } else {
      this.clientForm = this.formBuilder.group({
        allowed_cors_origins: [[], Validators.required],
        audience: [[], Validators.required],
        client_name: ['', Validators.required],
        client_secret: [''],
        client_uri: [''],
        contacts: [[]],
        grant_types: [[], Validators.required],
        logo_uri: [''],
        policy_uri: [''],
        redirect_uris: [[]],
        scope: [[]],
        tos_uri: [''],
        token_endpoint_auth_method: ['', Validators.required],
      });
    }
    if (this.clientForm) {
      this.subscription.add(
        this.clientForm.get('token_endpoint_auth_method')?.valueChanges
          .subscribe({
            next: (data) => {
              this.tokenEndpointAuthMethod = data;
            }
          })
      );
    }
  }

  generateSecret(): void {
    this.clientForm.get('client_secret')?.patchValue(window.crypto.randomUUID());
  }

  submit(): void {
    if (this.clientForm.invalid) {
      this.clientForm.markAllAsTouched();
      this.toastRef.show('Error in form');
      return;
    }
    const values = Object.assign({}, this.clientForm.value);
    if (this.tokenEndpointAuthMethod === 'none' || this.tokenEndpointAuthMethod === 'private_key_jwt') {
      values.client_secret = undefined;
    }

    values.scope = values.scope.join(' ');
    // console.log(values);
    if (this.editMode) {
      const jsonPatchList = Object.keys(this.clientForm.controls)
        .filter(key => {
          if (this.clientForm.controls[key].dirty) {
            const client = this.client as {[key: string]: any};
            console.log(client[key], values[key]);
            if (client[key] && values[key]) {
              return JSON.stringify(client[key]) !== JSON.stringify(values[key]);
            }
            else
            {
              return client[key] !== values[key];
            }
          }
          return false;
        })
        .map(key => {
          return {
            op: 'replace',
            path: '/' + key,
            value: values[key],
          };
        });
      // console.log(jsonPatchList);
      this.subscription.add(
        this.bangumiProvider.patchMyOAuth2Client(String(this.client.client_id), jsonPatchList).subscribe({
          next: (data) => {
            this.client = data;
            this.submitted = true;
          }
        })
      );
    } else {
      this.subscription.add(
        this.bangumiProvider.createMyOAuth2Client(values)
          .subscribe({
            next: (data) => {
              this.client = data;
              this.submitted = true;
            }
          })
      );
    }
  }

  async cancel(): Promise<void> {
    await this.router.navigate(['settings', 'developer']);
  }

  navigateToServiceClient(): void {
    this.router.navigate(['settings', 'developer', 'client', this.client.client_id, 'service']).then(() => {});
  }
}
